'use client';

import { getFragmentData } from '@packages/gql/generated/shopping';
import { RecoProductCardFragmentDoc } from '@packages/gql/generated/shopping/RecoProductCardFragmentDoc';
import { SeriesProductsDocument } from '@packages/gql/generated/shopping/SeriesProductsDocument';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useQuery } from 'urql';
import { useRecoElement } from '../../../hooks/useRecoElement';
import type { ProductsRecoSliderProps, RdeType, RequestParams } from '../../../types';
import { RecoCarousel } from '../../RecoCarousel';
import { RecoProductCard } from '../../RecoProductCard/RecoProductCardV2.client';
import { useProductsRecoTracking } from '../../RecoProductCard/useProductsRecoTracking';

export type SeriesProductsRecoSliderProps = Pick<RequestParams, 'productId'> &
  Omit<ProductsRecoSliderProps, 'preferTitle'>;

/* GraphQL */ `
  query SeriesProducts($productId: String!) {
    seriesProducts(productId: $productId) {
      type
      recommendations {
        ...RecoProductCard
      }
    }
  }
`;

const RDE_TYPE: RdeType = 'seriesProducts';

/**
 * SeriesProductsRecoSlider component description displayed in storybook
 * */
export const SeriesProductsRecoSlider: FC<SeriesProductsRecoSliderProps> = ({
  backgroundColor,
  elementIndex = 0,
  fetchOnThreshold = false,
  onProductClick,
  onSeen,
  pageTemplate,
  productId,
  title,
}) => {
  const { isInThreshold, isInView, recoEl } = useRecoElement();

  const [{ data, error }] = useQuery({
    query: SeriesProductsDocument,
    variables: {
      productId,
    },
    context: useMemo(() => ({ suspense: false }), []),
    pause: fetchOnThreshold ? !isInThreshold : false,
    requestPolicy: 'network-only',
  });

  const recommendations = data ? data.seriesProducts.recommendations : undefined;

  /** Filtered out recommendations that don't have a product (product === null) */
  const filteredRecommendations = recommendations
    ? recommendations.filter(
        (recommendation) => getFragmentData(RecoProductCardFragmentDoc, recommendation).product,
      )
    : undefined;

  const rdeTypeAbbr = data?.seriesProducts.type;

  useProductsRecoTracking({
    elementIndex,
    pageTemplate,
    rdeType: RDE_TYPE,
    rdeTypeAbbr,
    recommendations: filteredRecommendations,
    title,
    trackingCondition: isInView && !!filteredRecommendations && filteredRecommendations.length > 0,
    onSeen,
  });

  if (
    (error && !filteredRecommendations) ||
    (filteredRecommendations && filteredRecommendations.length === 0)
  ) {
    return null;
  }

  return (
    <RecoCarousel
      title={title}
      loadingState={!filteredRecommendations}
      backgroundColor={backgroundColor}
      ref={recoEl}
    >
      {filteredRecommendations?.map((recommendation, idx) => (
        <RecoProductCard
          data={recommendation}
          // rule is disabled because order doesn't change between rerenders
          // eslint-disable-next-line react/no-array-index-key
          key={idx}
          // pageTemplate={pageTemplate}
          // elementIndex={elementIndex}
          // position={idx}
          // productsLength={filteredRecommendations.length}
          // rdeType={RDE_TYPE}
          onClick={(akl, sku) => {
            onProductClick?.(akl, sku);
          }}
        />
      ))}
    </RecoCarousel>
  );
};
